import {
    AdminOptionsState,
    ToggleRegionCodeConfirmationRequiredOptionAction,
    ToggleAbleToSeeAndModifyExistingPatientDataOptionAction,
    ToggleAbleToSeeAndModifyExistingPreferredPharmacyOptionAction,
    ToggleDisplayProfilePicturesDuringAppointmentConfirmationOptionAction,
    ToggleHealthCardExpirationRedirectToReceptionOptionAction,
    UpdateLanguageSelectionListAction,
    ChangeDefaultLanguageAction,
    ChangeAdminSettingNumberFieldAction,
    TogglePatientAbleToBookNewAppointmentAction,
    SelectCurrentSiteAction,
    UpdateSiteSelectionListAction
} from "./type";
import LocalStorageManager from "../../services/LocalStorageManager";
import { LanguageCodes } from "../../constants/languages";
import { LoadClinicInformationAction } from "../ClinicInformation/type";

/**
 *
 * @name ToggleAbleToSeeAndModifyExistingPatientDataStateHandler
 * @export
 * @param {AdminOptionsState} state
 * @param {ToggleAbleToSeeAndModifyExistingPatientDataOptionAction} action
 * @returns {AdminOptionsState}
 */
export function ToggleAbleToSeeAndModifyExistingPatientDataStateHandler(
    state: AdminOptionsState,
    action: ToggleAbleToSeeAndModifyExistingPatientDataOptionAction
): AdminOptionsState {
    const newState = Object.assign({}, state, { ableToSeeAndModifyExistingPatientData: action.payload.value });
    LocalStorageManager.setAdminOptions(newState);
    return newState;
}

/**
 *
 * @name ToggleAbleToSeeAndModifyExistingPreferredPharmacyHandler
 * @export
 * @param {AdminOptionsState} state
 * @param {ToggleAbleToSeeAndModifyExistingPreferredPharmacyOptionAction} action
 * @returns {AdminOptionsState}
 */
export function ToggleAbleToSeeAndModifyExistingPreferredPharmacyStateHandler(
    state: AdminOptionsState,
    action: ToggleAbleToSeeAndModifyExistingPreferredPharmacyOptionAction
): AdminOptionsState {
    const newState = Object.assign({}, state, { ableToSeeAndModifyExistingPreferredPharmacy: action.payload.value });
    LocalStorageManager.setAdminOptions(newState);
    return newState;
}

/**
 * @name ToggleRegionCodeConfirmationRequiredStateHandler
 * @export
 * @param {AdminOptionsState} state
 * @param {ToggleRegionCodeConfirmationRequiredOptionAction} action
 * @returns {AdminOptionsState}
 */
export function ToggleRegionCodeConfirmationRequiredStateHandler(
    state: AdminOptionsState,
    action: ToggleRegionCodeConfirmationRequiredOptionAction
): AdminOptionsState {
    const newState = Object.assign({}, state, { regionCodeConfirmationRequired: action.payload.value });
    LocalStorageManager.setAdminOptions(newState);
    return newState;
}

export function ToggleDisplayProfilePicturesDuringAppointmentConfirmationHandler(
    state: AdminOptionsState,
    action: ToggleDisplayProfilePicturesDuringAppointmentConfirmationOptionAction
) {
    const newState = Object.assign({}, state, { showProfilePictureDuringConfirmation: action.payload.value });
    LocalStorageManager.setAdminOptions(newState);
    return newState;
}

export function ToggleHealthCardExpirationRedirectToReceptionHandler(
    state: AdminOptionsState,
    action: ToggleHealthCardExpirationRedirectToReceptionOptionAction
) {
    const newState = Object.assign({}, state, { healthCardExpirationRedirectToReception: action.payload.value });
    LocalStorageManager.setAdminOptions(newState);
    return newState;
}

export function TogglePatientAbleToBookNewAppointmentActionHandler(
    state: AdminOptionsState,
    action: TogglePatientAbleToBookNewAppointmentAction
) {
    const newState = Object.assign({}, state, { patientAbleToBookNewAppointments: action.payload.value });
    LocalStorageManager.setAdminOptions(newState);
    return newState;
}

export function UpdateLanguageSelectionListHandler(
    state: AdminOptionsState,
    action: UpdateLanguageSelectionListAction
) {
    const useableLanguages = new Set<LanguageCodes>(state.useableLanguages);

    if (action.payload.checked) {
        useableLanguages.add(action.payload.languageCode);
    } else {
        useableLanguages.delete(action.payload.languageCode);
    }
    let defaultLanguage = state.defaultLanguage;
    if (defaultLanguage) {
        if (!useableLanguages.has(defaultLanguage)) {
            if (useableLanguages.size === 1) {
                defaultLanguage = useableLanguages.values().next().value;
            } else if (useableLanguages.size === 0) {
                defaultLanguage = LanguageCodes.French;
            } else {
                if (!useableLanguages.has(defaultLanguage)) {
                    defaultLanguage = useableLanguages.values().next().value;
                }
            }
        }
    } else {
        if (useableLanguages.size >= 1) {
            defaultLanguage = useableLanguages.values().next().value;
        } else {
            defaultLanguage = LanguageCodes.French;
        }
    }
    const newState = Object.assign({}, state, { useableLanguages, defaultLanguage });
    LocalStorageManager.setAdminOptions(newState);
    return newState;
}

export function ChangeDefaultLanguageHandler(state: AdminOptionsState, action: ChangeDefaultLanguageAction) {
    if (state.useableLanguages.has(action.payload.lang)) {
        const newState = Object.assign({}, state, { defaultLanguage: action.payload.lang });
        LocalStorageManager.setAdminOptions(newState);
        return newState;
    }
    return state;
}

export function ChangeAdminSettingNumberFieldHandler(state: AdminOptionsState, action: ChangeAdminSettingNumberFieldAction) {
    const newState = Object.assign({}, state, { [action.payload.field]: action.payload.value });
    LocalStorageManager.setAdminOptions(newState);
    return newState;
}

export function ChangeAdminCurrentSiteHandler(state: AdminOptionsState, action: SelectCurrentSiteAction) {
    const newState = Object.assign({}, state, { siteId: action.payload.selectedSiteId });
    LocalStorageManager.setAdminOptions(newState);
    return newState;
}

export function UpdateSiteSelectionListHandler(state: AdminOptionsState, action: UpdateSiteSelectionListAction) {
    const multiSiteIds = new Set<string>(state.multiSiteIds);
        if (action.payload.checked) {
        multiSiteIds.add(action.payload.selectedSiteId.toString());
    } else {
        multiSiteIds.delete(action.payload.selectedSiteId.toString());
    }
    const newState = Object.assign({}, state, { multiSiteIds });
    LocalStorageManager.setAdminOptions(newState);
    return newState;
}

export function LoadClinicInformationHandler(state: AdminOptionsState, action: LoadClinicInformationAction) {
    const featureFlags = action.payload.featureFlags;
    const newState = Object.assign({}, state, {
        ableToSeeAndModifyExistingPatientData: featureFlags.patientInformationModificationEnabled && state.ableToSeeAndModifyExistingPatientData,
        ableToSeeAndModifyExistingPreferredPharmacy: featureFlags.preferredPharmacyEditionEnabled && state.ableToSeeAndModifyExistingPreferredPharmacy,
        regionCodeConfirmationRequired: featureFlags.regionCodeConfirmationEnabled && state.regionCodeConfirmationRequired,
        showProfilePictureDuringConfirmation: featureFlags.displayMyleProfilePictureEnabled && state.showProfilePictureDuringConfirmation,
        healthCardExpirationRedirectToReception: featureFlags.healthCardExpiryCheckinStopEnabled && state.healthCardExpirationRedirectToReception
    });

    if ((state.siteId && !action.payload.sites.some(site => site.id === state.siteId)) || !state.siteId) {
        newState.siteId = action.payload.sites.firstOrDefault().id;
    }

    LocalStorageManager.setAdminOptions(newState);
    return newState;
}


// WEBPACK FOOTER //
// ./src/store/AdminOptions/handlers.ts